<template>
  <div
    class="h-full flex"
    @mouseover="isHoveredOver = true"
    @mouseleave="isHoveredOver = false"
  >
    <div class="w-full relative bg-white">
      <div
        ref="editorContainer"
        class="h-full w-full flex items-start"
        @click="showLockWarning"
      >
        <FloatingMenu
          v-if="isAiSuggested"
          placement="bottom"
          :offset="[0, 8]"
          class="h-full"
        >
          <template #activator="{ show }">
            <button
              class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-3 text-gray-800 rounded-md p-2"
              :class="{
                'sticky top-27': expansionPanel?.isShown.value,
                'border border-gray-800': aiSuggestedNotReported,
                'border border-green-500':
                  !aiSuggestedNotReported && isAiSuggested,
              }"
              :title="finalizeButtonTitle"
              @click="show"
            >
              <MinusIcon v-if="isAnswerEmpty" class="w-4" />
              <CheckIcon v-else class="w-4" />
              <SparklesIcon class="w-4" />
            </button>
          </template>
          <template #content="{ hide }">
            <div class="flex flex-col bg-white rounded-lg shadow-lg py-2">
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 font-medium text-gray-900"
                @click="acceptAiSuggestion(hide)"
              >
                Accept AI suggestion
              </button>
              <button
                class="px-4 py-3 text-left hover:bg-gray-50 font-medium text-gray-900"
                @click="rejectAiSuggestion(hide)"
              >
                Reject AI suggestion
              </button>
            </div>
          </template>
        </FloatingMenu>
        <button
          v-else
          class="w-7.5 ml-2 mt-3 bg-gray-100 flex flex-col items-center justify-center gap-2 rounded-md text-gray-800 p-2"
          :class="{
            'sticky top-27': expansionPanel?.isShown.value,
            'bg-gray-800 text-white': isAnswerEmpty && isFinalized,
            'bg-green-500 text-white': !isAnswerEmpty && isFinalized,
          }"
          :title="finalizeButtonTitle"
          @click="toggleReviewItemSynthesisAttributeFinalization"
        >
          <MinusIcon v-if="isAnswerEmpty" class="w-4" />
          <CheckIcon v-else class="w-4" />
        </button>
        <div class="flex flex-col w-full h-full">
          <Editor
            v-if="isVisible"
            v-model:html="answer"
            :provider="dataExtractionProvider"
            :user-name="userName"
            :user-color="color"
            :disabled="
              review.isReviewReadonly.value || isFinalized || isAiSuggested
            "
            :field="
              'study::' + study.id + '|attribute::' + attributeStructure.id
            "
            class="flex min-h-0 w-full"
            :class="[isExpanded ? 'h-full' : 'h-32']"
            editor-classes=" p-3 hover:overflow-auto overflow-hidden pb-10 h-full w-full"
            placeholder="Enter the information extracted from the record. if the record does not contain information, click confirm."
            @click="expansionPanel?.expand()"
          />
        </div>
      </div>
      <div
        v-if="!review.isReviewReadonly.value"
        class="absolute top-10 -right-4 items-center"
      >
        <div v-if="!isFinalized">
          <Transition name="fade">
            <div
              v-show="
                (!review.isReviewReadonly.value && isHoveredOver) ||
                isGeneratingAnswer
              "
            >
              <StudyQA
                v-model:is-generating-answer="isGeneratingAnswer"
                :study="study"
                :attribute-structure="attributeStructure"
                :query="attributeStructure.question ?? ''"
                :is-review-readonly="review.isReviewReadonly.value"
              />
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ReviewItem } from '@core/domain/models/reviewItem.model'
import StudyQA from './StudyQA.vue'
import { AttributeStructure } from '@core/domain/models/data-extraction-plan-attribute'
import { injectStrict } from '@app/utils/injectStrict'
import { ReviewKey } from '../../../use-review'
import useSnackbar from '@app/composables/use-snackbar'
import useLoading from '@app/composables/use-loading'
import { HttpException } from '@core/exceptions/http.exception'
import { SnackbarState } from '@app/types'
import { Role } from '@core/domain/types/role.type'
import { ReviewLockState } from '@core/domain/types/reviewLockState.type'
import { errorMessage } from '@app/utils/error-message'
import { computed, onMounted, ref, useTemplateRef } from 'vue'
import Editor from '../../components/Editor.vue'
import { AuthKey } from '@app/injectionKeys'
import { SparklesIcon, CheckIcon, MinusIcon } from '@heroicons/vue/24/solid'
import FloatingMenu from '@app/components/Global/FloatingMenu.vue'

const snackbar = useSnackbar()
const loading = useLoading()
const isHoveredOver = ref(false)
const props = withDefaults(
  defineProps<{
    attributeStructure: AttributeStructure
    study: ReviewItem
    isExpanded: boolean
    isReviewReadOnly?: boolean
    expansionPanel?: any
  }>(),
  {
    isReviewReadOnly: false,
  },
)
const isGeneratingAnswer = ref(false)
const review = injectStrict(ReviewKey)

const answer = defineModel<string>()

const isAnswerEmpty = computed(() => {
  if (!answer.value) return true
  const text = answer.value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, '')
    .trim()

  return text.length === 0 || text === 'Not reported'
})

const currentAttribute = computed(() =>
  props.study.synthesis?.attributes?.find(
    (attr) => attr.attributeStructureId === props.attributeStructure.id,
  ),
)

const isFinalized = computed(() => currentAttribute.value?.isFinalized ?? false)

const isAiSuggested = computed(
  () => currentAttribute.value?.isAiSuggested ?? false,
)

const aiSuggestedNotReported = computed(
  () => currentAttribute.value?.aiSuggestedNotReported ?? false,
)

const auth = injectStrict(AuthKey)

function showLockWarning() {
  if (!review.isReviewReadonly.value) return
  const isAdmin = auth.user.value?.role === Role.ADMIN
  let message = ''
  if (
    review.entity.value.plan?.lockState === ReviewLockState.UNLOCKED &&
    !review.isLocked.value
  ) {
    message = isAdmin
      ? 'The plan is unlocked, lock it to make changes.'
      : 'The plan is unlocked. Please contact an admin to make changes.'
  } else if (review.isLocked.value) {
    message = isAdmin
      ? 'The review is locked, unlock it to make changes.'
      : 'The review is locked. Please contact an admin to make changes.'
  }
  if (message) {
    snackbar.show(SnackbarState.ERROR, message)
  }
}

async function toggleReviewItemSynthesisAttributeFinalization() {
  loading.start()
  try {
    if (isFinalized.value) {
      await unfinalizeReviewItemSynthesisAttribute()
    } else {
      await finalizeReviewItemSynthesisAttribute()
    }
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    loading.stop()
  }
}

async function acceptAiSuggestion(hide: () => void) {
  await finalizeReviewItemSynthesisAttribute()
  hide()
}

async function rejectAiSuggestion(hide: () => void) {
  loading.start()
  try {
    await review.clearAttributeAiSuggestion(
      props.study.id,
      props.attributeStructure.id,
    )
    hide()
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    loading.stop()
  }
}

async function finalizeReviewItemSynthesisAttribute() {
  loading.start()
  try {
    await review.finalizeReviewItemSynthesisAttribute(
      props.study.id,
      props.attributeStructure.id,
    )
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    loading.stop()
  }
}

async function unfinalizeReviewItemSynthesisAttribute() {
  loading.start()
  try {
    await review.unfinalizeReviewItemSynthesisAttribute(
      props.study.id,
      props.attributeStructure.id,
    )
    snackbar.success('Success')
  } catch (e) {
    const error = e as HttpException
    snackbar.show(SnackbarState.ERROR, errorMessage(error.response.data))
    if (error.response.data.statusCode >= 500) {
      throw e
    }
  } finally {
    loading.stop()
  }
}

const { user, color } = injectStrict(AuthKey)
const userName = computed(
  () => user.value?.firstName + ' ' + user.value?.lastName,
)
const { dataExtractionProvider } = injectStrict(ReviewKey)

const isVisible = ref(false)
const editorContainer = useTemplateRef('editorContainer')
const finalizeButtonTitle = computed(() => {
  let message = ''
  if (isFinalized.value) {
    message = 'Edit extraction'
  } else {
    message = isAnswerEmpty.value
      ? 'Mark as not reported'
      : 'Confirm extraction'
  }
  return message
})
onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        isVisible.value = true
        observer.disconnect()
      }
    },
    {
      rootMargin: '50px',
      threshold: 0.1,
    },
  )

  if (editorContainer.value) {
    observer.observe(editorContainer.value)
  }
})
</script>

<style>
.loader {
  border: 4px solid var(--color-white);
  border-bottom-color: transparent;
  border-radius: 50%;
  display: inline-block;
  box-sizing: border-box;
  animation: rotation 1s linear infinite;
}

@keyframes rotation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
